import { ArrowUp, ArrowDown, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

// The dashboard sections a learner can hide or move, in the default order.
// The ids must match DASHBOARD_SECTION_IDS in backend/server.py.
export const DASHBOARD_SECTIONS = [
    { id: 'stats', label: 'Statistics' },
    { id: 'today', label: "Today's minute goal" },
    { id: 'streak', label: 'Consecutive day streak' },
    { id: 'week', label: 'Weekly practice target' },
    { id: 'heatmap', label: 'Monthly days practised' },
    { id: 'weak_phrases', label: 'Phrases with lowest scores' },
    { id: 'weak_words', label: 'Words with lowest scores' },
    { id: 'weak_sounds', label: 'Sounds with lowest scores' },
    { id: 'top_collections', label: 'Least practised collections' },
];

// The saved order, then anything not in it (a section added later) in the
// default order, so a new section never goes missing from an old layout.
export function orderSections(savedOrder = []) {
    const known = DASHBOARD_SECTIONS.map(s => s.id);
    const kept = savedOrder.filter(id => known.includes(id));
    return [...kept, ...known.filter(id => !kept.includes(id))];
}

// Up and down buttons rather than drag and drop: dragging is unreliable on a
// phone, and the buttons work from the keyboard too.
export default function DashboardCustomise({ open, onOpenChange, layout, onChange, showSounds }) {
    const order = orderSections(layout.order).filter(id => showSounds || id !== 'weak_sounds');
    const hidden = layout.hidden;
    const label = (id) => DASHBOARD_SECTIONS.find(s => s.id === id)?.label || id;

    const move = (id, delta) => {
        const full = orderSections(layout.order);
        const from = full.indexOf(id);
        // Move past the next visible-in-this-list neighbour, so a section
        // that is not offered (Sounds, for most accounts) is never swapped with.
        let to = from + delta;
        while (to >= 0 && to < full.length && !order.includes(full[to])) to += delta;
        if (to < 0 || to >= full.length) return;
        const next = [...full];
        next.splice(from, 1);
        next.splice(to, 0, id);
        onChange({ order: next, hidden });
    };

    const toggle = (id) => onChange({
        order: orderSections(layout.order),
        hidden: hidden.includes(id) ? hidden.filter(x => x !== id) : [...hidden, id],
    });

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto" aria-describedby="customise-description">
                <DialogHeader>
                    <DialogTitle>Customise your dashboard</DialogTitle>
                    <DialogDescription id="customise-description">
                        Switch sections off, or move them up and down. It's saved to your account.
                    </DialogDescription>
                </DialogHeader>
                <ul className="divide-y divide-stone-100 border border-stone-100 rounded-xl">
                    {order.map((id, i) => {
                        const on = !hidden.includes(id);
                        return (
                            <li key={id} data-testid={`customise-row-${id}`} className="flex items-center gap-2 px-3 py-2">
                                <Switch checked={on} onCheckedChange={() => toggle(id)} aria-label={`Show ${label(id)}`} />
                                <span className={`flex-1 min-w-0 text-sm ${on ? 'text-stone-800' : 'text-stone-400'}`}>{label(id)}</span>
                                <button
                                    type="button"
                                    aria-label={`Move ${label(id)} up`}
                                    disabled={i === 0}
                                    onClick={() => move(id, -1)}
                                    className="h-9 w-9 flex items-center justify-center rounded-lg text-stone-600 hover:bg-stone-100 disabled:opacity-30 disabled:hover:bg-transparent"
                                >
                                    <ArrowUp className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    aria-label={`Move ${label(id)} down`}
                                    disabled={i === order.length - 1}
                                    onClick={() => move(id, 1)}
                                    className="h-9 w-9 flex items-center justify-center rounded-lg text-stone-600 hover:bg-stone-100 disabled:opacity-30 disabled:hover:bg-transparent"
                                >
                                    <ArrowDown className="h-4 w-4" />
                                </button>
                            </li>
                        );
                    })}
                </ul>
                <Button
                    type="button"
                    variant="outline"
                    className="w-full rounded-full"
                    onClick={() => onChange({ order: [], hidden: [] })}
                >
                    <RotateCcw className="h-4 w-4 mr-1" />Back to the original layout
                </Button>
            </DialogContent>
        </Dialog>
    );
}
